// api.js (updated)
import axios from "axios";
import { encryptAESCryptoJS, decryptAESCryptoJS } from "../crypto";

// Create axios instance
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Add token automatically if stored
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to encrypt sensitive user fields
const encryptSensitiveFields = (data) => {
  const sensitiveFields = ["FullName", "ContactNumber", "EMailID", "Username", "Password"];
  const encryptedData = { ...data };

  sensitiveFields.forEach((field) => {
    if (encryptedData[field]) {
      encryptedData[field] = encryptAESCryptoJS(encryptedData[field]);
    }
  });

  return encryptedData;
};


// Helper function to decrypt sensitive user fields
const decryptSensitiveFields = (data) => {
  const sensitiveFields = ["FullName", "ContactNumber", "EMailID", "DOB", "Username"];
  const decryptedData = { ...data };
  sensitiveFields.forEach((field) => {
    if (decryptedData[field]) {
      try {
        decryptedData[field] = decryptAESCryptoJS(decryptedData[field]);
      } catch (e) {
        console.error(`Failed to decrypt ${field}:`, e);
      }
    }
  });
  return decryptedData;
};

// Helper function to encrypt formation fields
const encryptFormationFields = (data) => {
  const sensitiveFields = ["Category", "Question", "Answer"];
  const encryptedData = { ...data };
  sensitiveFields.forEach((field) => {
    if (encryptedData[field]) {
      encryptedData[field] = encryptAESCryptoJS(encryptedData[field]);
    }
  });
  return encryptedData;
};

// Helper function to decrypt formation fields
const decryptFormationFields = (data) => {
  const sensitiveFields = ["Category", "Question", "Answer"];
  const decryptedData = { ...data };
  sensitiveFields.forEach((field) => {
    if (decryptedData[field]) {
      try {
        decryptedData[field] = decryptAESCryptoJS(decryptedData[field]);
      } catch (e) {
        console.error(`Failed to decrypt ${field}:`, e);
      }
    }
  });
  return decryptedData;
};

// Helper function to encrypt recommendation fields
const encryptRecommendationFields = (data) => {
  const sensitiveFields = ["Sense", "Label", "Course", "AssetClass", "Comments"];
  const encryptedData = { ...data };
  sensitiveFields.forEach((field) => {
    if (encryptedData[field]) {
      encryptedData[field] = encryptAESCryptoJS(encryptedData[field]);
    }
  });
  return encryptedData;
};

// Helper function to decrypt recommendation fields
const decryptRecommendationFields = (data) => {
  const sensitiveFields = ["Sense", "Label", "Course", "AssetClass", "Comments"];
  const decryptedData = { ...data };
  sensitiveFields.forEach((field) => {
    if (decryptedData[field]) {
      try {
        decryptedData[field] = decryptAESCryptoJS(decryptedData[field]);
      } catch (e) {
        console.error(`Failed to decrypt ${field}:`, e);
      }
    }
  });
  return decryptedData;
};

// Create a new user
export const createUser = async (userData) => {
  const encryptedData = encryptSensitiveFields({
    ...userData,
    ProfileCreationDate: new Date().toISOString().split("T")[0],
    EmailAlert: userData.EmailAlert ?? true,
    PushAlert: userData.PushAlert ?? false,
  });

  const response = await api.post("/admin/user", encryptedData);
  return response.data;
};

// Update an existing user
export const updateUser = async (userId, userData) => {
  const encryptedData = encryptSensitiveFields({
    ...userData,
    Password: userData.Password || undefined, // do not overwrite with empty
    ProfileCreationDate: userData.ProfileCreationDate || new Date().toISOString().split("T")[0],
    EmailAlert: userData.EmailAlert ?? true,
    PushAlert: userData.PushAlert ?? false,
  });

  const response = await api.put(`/admin/user/${userId}`, encryptedData);
  return response.data;
};

// Delete a user
export const deleteUser = async (userId) => {
  const response = await api.delete(`/admin/user/${userId}`);
  return response.data;
};

// Fetch all users
export const getUsers = async () => {
  const response = await api.get("/admin/user");
  console.log("RAW USERS FROM BACKEND:", response.data.Data);
  const users = response.data.Data.map((user) => ({
    id: user.ID,
    fullName: user.FullName ? decryptAESCryptoJS(user.FullName) : "",
    firstName: user.FirstName ? decryptAESCryptoJS(user.FirstName) : "",
    lastName: user.LastName ? decryptAESCryptoJS(user.LastName) : "",
    contactNumber: user.ContactNumber ? decryptAESCryptoJS(user.ContactNumber) : "",
    email: user.EmailID ? decryptAESCryptoJS(user.EmailID) : "",
    dob: user.DOB || "", // Make sure this is mapped
    dateOfBirth: user.DOB || "", // Keep both for compatibility
    username: user.Username ? decryptAESCryptoJS(user.Username) : "",
    status: user.UserRole === "ADMINUSER" ? "Active" : "Inactive",
    profileCreationDate: user.ProfileCreationDate,
    emailAlert: user.EmailAlert === 1 || user.EmailAlert === true,
    pushAlert: user.PushAlert === 1 || user.PushAlert === true,
    userID: user.ID, // Add this for consistency
    subscriptionEndingDate: user.ActiveSubscriptionEndDate || user.DebutDate || null,
    InvoiceHistory: user.InvoiceHistory || [],
    // Keep original data for debugging
    _raw: user
  }));
  console.log('users', users);
  return users;
};

export const getAdminUsers = async () => {
  try {
    const response = await api.get("/admin/user", {
      params: {
        UserRole: 'ADMINUSER'
      }
    });
    
    console.log("RAW ADMIN USERS FROM BACKEND:", response.data.Data);
    
    const users = response.data.Data.map((user) => ({
      id: user.ID,
      fullName: user.FullName ? decryptAESCryptoJS(user.FullName) : "",
      contactNumber: user.ContactNumber ? decryptAESCryptoJS(user.ContactNumber) : "",
      email: user.EmailID ? decryptAESCryptoJS(user.EmailID) : "",
      dateOfBirth: user.DOB || "",
      username: user.Username ? decryptAESCryptoJS(user.Username) : "",
      status: user.UserRole === "ADMINUSER" ? "Active" : "Inactive",
      password: user.Password ? decryptAESCryptoJS(user.Password) : "",
      userRole: user.UserRole,
      profileCreationDate: user.ProfileCreationDate,
      emailAlert: user.EmailAlert,
      pushAlert: user.PushAlert,
      subscriptionEndingDate: user.SubscriptionEndingDate || null,
    }));
    
    console.log('Admin Users:', users);
    return users;
  } catch (error) {
    console.error('Error fetching admin users:', error);
    throw error;
  }
};

export const getAppUsers = async () => {
  const response = await api.get("/admin/user");
  const users = response.data.Data.map((user) => ({
    id: user.ID,
    fullName: user.FullName ? decryptAESCryptoJS(user.FullName) : "",
    firstName: user.FirstName ? decryptAESCryptoJS(user.FirstName) : "",
    lastName: user.LastName ? decryptAESCryptoJS(user.LastName) : "",
    contactNumber: user.ContactNumber ? decryptAESCryptoJS(user.ContactNumber) : "",
    email: user.EmailID ? decryptAESCryptoJS(user.EmailID) : "",
    dob: user.DOB ? decryptAESCryptoJS(user.DOB) : "",
    username: user.Username ? decryptAESCryptoJS(user.Username) : "",
    status: user.UserRole === "ADMINUSER" ? "Active" : "Inactive",
    profileCreationDate: user.ProfileCreationDate,
    emailAlert: user.EmailAlert === 1 || user.EmailAlert === true,
    pushAlert: user.PushAlert === 1 || user.PushAlert === true,
    subscriptionEndingDate: user.ActiveSubscriptionEndDate || user.DebutDate || null,
    InvoiceHistory: user.InvoiceHistory || [],
  }));
  return users;
};

const parseBooleanActive = (val) => {
  if (val === false || val === 0 || val === '0' || val === 'false' || val === 'False') return false;
  if (val === true || val === 1 || val === '1' || val === 'true' || val === 'True') return true;
  return true;
};

const normalizeAppUserRecord = (user) => {
  const rawIsActive = user?.IsActive !== undefined
    ? user.IsActive
    : (user?.isActive !== undefined
      ? user.isActive
      : (user?.is_active !== undefined
        ? user.is_active
        : (user?._raw?.IsActive !== undefined
          ? user._raw.IsActive
          : (user?._raw?.isActive !== undefined ? user._raw.isActive : undefined))));
  const parsedIsActive = parseBooleanActive(rawIsActive);

  return {
    id: user.ID,
    userID: user.ID,
    fullName: user.FullName ? decryptAESCryptoJS(user.FullName) : "",
    firstName: user.FirstName ? decryptAESCryptoJS(user.FirstName) : "",
    lastName: user.LastName ? decryptAESCryptoJS(user.LastName) : "",
    contactNumber: user.ContactNumber ? decryptAESCryptoJS(user.ContactNumber) : "",
    email: user.EmailID ? decryptAESCryptoJS(user.EmailID) : "",
    dob: user.DOB ? decryptAESCryptoJS(user.DOB) : "",
    username: user.Username ? decryptAESCryptoJS(user.Username) : "",
    status: user.UserRole === "ADMINUSER" ? "Active" : "Inactive",
    userRole: user.UserRole || "",
    profileCreationDate: user.ProfileCreationDate || "",
    emailAlert: user.EMailAlert === 1 || user.EmailAlert === 1 || user.EmailAlert === true,
    pushAlert: user.PushAlert === 1 || user.PushAlert === true,
    os: user.OS || "",
    isActive: parsedIsActive,
    IsActive: parsedIsActive,
    subscriptionEndingDate: user.SubscriptionEndDate || user.ActiveSubscriptionEndDate || user.DebutDate || null,
    InvoiceHistory: user.InvoiceHistory || [],
    hasActiveSubscription: user.HasActiveSubscription === true,
    isRegisteredFromAdminPanel: user.IsRegisteredFromAdminPanel === true || user.IsRegisteredFromAdminPanel === 1 || user.IsRegisteredFromAdminPanel === "1",
    loginType: user["Login Type"] || user.LoginType || "",
    _raw: user
  };
};

// CREATE FAQ
export const createFaq = async (faqData) => {
  const response = await api.post("/admin/formation", faqData);
  return response.data;
};

// UPDATE FAQ
export const updateFaq = async (formationId, faqData) => {
  const response = await api.put(`/admin/formation/${formationId}`, faqData);
  return response.data;
};

// DELETE FAQ
export const deleteFaq = async (formationId) => {
  const response = await api.delete(`/admin/formation/${formationId}`);
  return response.data;
};

// GET ALL FAQS
export const getFaqs = async () => {
  const response = await api.get("/admin/formation");

  const validCategories = ["Investir", "Actions", "Obligations", "Or"];

  const formations = response.data.Data.map((formation) => ({
    id: formation.ID,
    formationCategory: validCategories.includes(formation.Category) 
      ? formation.Category 
      : "Investir",

    question: formation.Question || "",
    answer: formation.Answer || "",
    isActive: formation.IsActive || "Yes",
  }));

  return formations;
};


// Create a new recommendation
export const createRecommendation = async (recommendationData) => {
  const encryptedData = ({
    ...recommendationData,
    Month: parseInt(recommendationData.Month, 10), // Ensure Month is an integer
    Year: parseInt(recommendationData.Year, 10), // Ensure Year is an integer
  });
  const response = await api.post("/admin/recommendation", encryptedData);
  return response.data;
};

// Update an existing recommendation
export const updateRecommendation = async (recommendationId, recommendationData) => {
  const encryptedData = ({
    ...recommendationData,
    Month: parseInt(recommendationData.Month, 10), // Ensure Month is an integer
    Year: parseInt(recommendationData.Year, 10), // Ensure Year is an integer
  });
  const response = await api.put(`/admin/recommendation/${recommendationId}`, encryptedData);
  return response.data;
};

// Delete a recommendation
export const deleteRecommendation = async (recommendationId) => {
  const response = await api.delete(`/admin/recommendation/${recommendationId}`);
  return response.data;
};

// Fetch all recommendations
export const getRecommendations = async () => {
  const response = await api.get("/admin/recommendation");

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Convert object with keys like "5" into array
  const list = Object.values(response.data.Data || {});

  const recommendations = list.map((rec) => ({
    id: rec.ID,
    month: months[rec.Month - 1] || rec.Month.toString(),
    year: rec.Year.toString(),
    sense: rec.Sense || "",
    libelle: rec.Label || "",
    cours: rec.Course || "",
    assetClass: rec.AssetClass || "",
    comments: rec.Comments || "",
  }));

  return recommendations;
};

// Sign in user
export const signIn = async (signInData) => {
  const encryptedData = {
    EMailID: encryptAESCryptoJS(signInData.EMailID),
    Password: encryptAESCryptoJS(signInData.Password),
    Platform: signInData.Platform || "Web",
    OSVersion: signInData.OSVersion || navigator.platform,
    Browser: signInData.Browser || navigator.userAgent,
    UserRole: signInData.UserRole || "ADMINUSER",
  };
  const response = await api.post("/auth/signin", encryptedData);
  const decryptedUser = decryptSensitiveFields(response.data.signupResult.user);
  return {
    message: response.data.Message,
    token: response.data.signupResult.token,
    user: decryptedUser,
    subscription: response.data.signupResult.subscription,
  };
};

export const uploadNotationFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/admin/upload/Notation/Add", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });

  return response.data;
};
export const getNotationList = async () => {
  const response = await api.get("/admin/upload/Notation/getList");
  return response.data;
};

// Upload Fichier Actions file
export const uploadFichierActions = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/admin/upload/FichierActions/Add", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });

  return response.data;
};

// Get Fichier Actions list
export const getFichierActionsList = async () => {
  const response = await api.get("/admin/upload/FichierActions/GetList");
  return response.data;
};

export const uploadFichierObligataire = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/admin/upload/FichierObligataire/Add", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });

  return response.data;
};
export const getFichierObligataireList = async () => {
  const response = await api.get("/admin/upload/FichierObligataire/GetList");
  return response.data;
};

export const addAmwalyPortfolio = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/AmwalyPortfolio/Add", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });

  return response.data;
};
export const getAmwalyPortfolioList = async () => {
  const response = await api.get("/AmwalyPortfolio/GetList");
  return response.data;
};

export const upsertAppParameters = async (data) => {
  const response = await api.post("/admin/AppParameters/Upsert", data);
  return response.data;
};

export const getAppParameters = async () => {
  const response = await api.get("/admin/AppParameters/GetAppParameters");
  return response.data;
};

export const getProfessionalSituation = async () => {
  const response = await api.get("/admin/professional/list");
  return response.data;
}

export const updateProfessionalSituation = async (data) => {
  const response = await api.put("/admin/professional/update", data);
  return response.data;
}

export const createProfessionalSituation = async (data) => {
  const response = await api.post("/admin/professional/add", data);
  return response.data;
}

export const getSubscriptions = async () => {
  const response = await api.get("/admin-Subscription-GetList");
  console.log("response.data", response.data.Data);
  return response.data.Data;
};

export const createSubscription = async (data) => {
  const response = await api.post("/create-subscription", data);
  return response.data;
};

export const updateSubscription = async (id, data) => {
  const response = await api.put(`/update-subscription`, data);
  return response.data;
};

export const saveColumnConfig = async (columns) => {
  try {
    const response = await api.post("/userconfig/savequerycolumnconfig", {
      columns: columns.map(col => ({
        ColumnName: col.ColumnName,
        Priority: col.Priority,
        IsVisible: col.IsVisible
      }))
    });
    return response.data;
  } catch (error) {
    console.error('Save Column Config Error:', error);
    throw error;
  }
};

export const saveAppUserColumnConfig = async (columns) => {
  try {
    const response = await api.post("/userconfig/savecolumnconfig", {
      columns: columns.map((col) => ({
        ColumnName: col.ColumnName,
        Priority: col.Priority,
        IsVisible: col.IsVisible
      }))
    });
    return response.data;
  } catch (error) {
    console.error('Save App User Column Config Error:', error);
    throw error;
  }
};

export const getUserQueriesReport = async (params) => {
  try {
    const requestBody = {
      FromDate: params.FromDate || '',
      ToDate: params.ToDate || '',
      IsPreLogin: params.IsPreLogin || '3',
      OutPutType: params.OutPutType || 'json'
    };

    // Add advanced filters if provided
    if (params.AdvanceFilter && params.AdvanceFilter.length > 0) {
      requestBody.AdvanceFilter = params.AdvanceFilter;
    }

    // Add sorting
    if (params.SortingColumn) {
      requestBody.SortingColumn = params.SortingColumn;
      requestBody.SortOrder = params.SortOrder || 'desc';
    }

    // Add pagination
    if (params.Page) {
      requestBody.Page = params.Page;
      requestBody.Limit = params.Limit || 10;
    }

    const response = await api.post("/admin/AdminGetUserQueries", requestBody);
    
    // Decrypt data if present
    if (response.data?.Data) {
      const decryptedData = response.data.Data.map(query => {
        const decryptedQuery = { ...query };
        
        if (decryptedQuery.EMailID) {
          try {
            decryptedQuery.EMailID = decryptAESCryptoJS(decryptedQuery.EMailID);
          } catch (e) {
            console.error('Failed to decrypt EMailID:', e);
          }
        }
        
        if (decryptedQuery.Objective) {
          try {
            decryptedQuery.Objective = decryptedQuery.Objective;
          } catch (e) {
            console.error('Failed to decrypt Objective:', e);
          }
        }
        
        if (decryptedQuery.Message) {
          try {
            decryptedQuery.Message = decryptedQuery.Message;
          } catch (e) {
            console.error('Failed to decrypt Message:', e);
          }
        }
        
        return decryptedQuery;
      });
      
      return {
        ...response.data,
        Data: decryptedData
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('User Queries Report API Error:', error);
    throw error;
  }
};

// Download Excel with filters
export const downloadQueriesExcel = async (params) => {
  try {
    const requestBody = {
      FromDate: params.FromDate || '',
      ToDate: params.ToDate || '',
      IsPreLogin: params.IsPreLogin || '3',
      OutPutType: 'excel'
    };

    // Add filters
    if (params.AdvanceFilter && params.AdvanceFilter.length > 0) {
      requestBody.AdvanceFilter = params.AdvanceFilter;
    }

    const response = await api.post("/admin/AdminGetUserQueries", requestBody, {
      responseType: 'json'
    });
    
    return response.data;
  } catch (error) {
    console.error('Excel Download Error:', error);
    throw error;
  }
};

// Download PDF with filters
export const downloadQueriesPdf = async (params) => {
  try {
    const requestBody = {
      FromDate: params.FromDate || '',
      ToDate: params.ToDate || '',
      IsPreLogin: params.IsPreLogin || '3',
      OutPutType: 'pdf'
    };

    // Add filters
    if (params.AdvanceFilter && params.AdvanceFilter.length > 0) {
      requestBody.AdvanceFilter = params.AdvanceFilter;
    }

    const response = await api.post("/admin/AdminGetUserQueries", requestBody, {
      responseType: 'json'
    });
    
    return response.data;
  } catch (error) {
    console.error('PDF Download Error:', error);
    throw error;
  }
};

export const getDashboardData = async () => {
  const response = await api.get("/admin/user/AdminDashboard");
  console.log("Dashboard data:", response.data);
  return response.data;
};


export const getAllNotification = async () => {
  const response = await api.get("/adminNotification/notification-get");
  return response.data;
}

export const sendNotification = async (notificationData) => {
  const response = await api.post("/adminNotification/notification-add", notificationData);
  return response.data;
}

export const checkValidEmail = async (email) => {
  const response = await api.get(`/adminNotification/CheckForValidEmail?MobileAppEmail=${encodeURIComponent(email)}`);
  console.log(response.data);
  return response.data;
}

export const updateUserProfile = async (userData) => {
  const payload = {
    ...userData,
    UserID: userData.UserID || userData.id || userData.ID,
  };

  // Encrypt sensitive user fields if present
  if (userData.EmailID) payload.EmailID = encryptAESCryptoJS(userData.EmailID);
  if (userData.ContactNumber) payload.ContactNumber = encryptAESCryptoJS(userData.ContactNumber);
  if (userData.MobileNumber) payload.MobileNumber = encryptAESCryptoJS(userData.MobileNumber);
  if (userData.DOB) payload.DOB = encryptAESCryptoJS(userData.DOB);
  if (userData.FirstName) payload.FirstName = encryptAESCryptoJS(userData.FirstName);
  if (userData.LastName !== undefined && userData.LastName !== null && userData.LastName !== '') {
    payload.LastName = encryptAESCryptoJS(userData.LastName);
  }
  if (userData.Password && typeof userData.Password === 'string' && userData.Password.trim()) {
    payload.Password = encryptAESCryptoJS(userData.Password.trim());
  } else {
    delete payload.Password;
  }

  // Remove undefined fields so they aren't sent
  Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

  const response = await api.put("/admin/user/UpdateAppUserProfileFromAdmin", payload);
  
  // Decrypt sensitive fields if they are returned in the response
  let decryptedUser = null;
  if (response.data?.user) {
    decryptedUser = decryptSensitiveFields(response.data.user);
  }

  return {
    message: response.data.Message || response.data.message,
    user: decryptedUser || response.data.user,
    ...response.data
  };
};

export const updateInvestmentCriteria = async (data) => {
  const response = await api.put("/admin/user/updateinvestmentcriteria", data);
  return response.data;
};

export const getAppUsersWithFilter = async (params) => {
  try {
    const response = await api.post("/admin/user/getappuserwithfilter", params);
    return {
      ...response.data,
      Data: Array.isArray(response.data?.Data)
        ? response.data.Data.map(normalizeAppUserRecord)
        : [],
      Config: response.data?.Config || [],
      UserConfig: response.data?.UserConfig || []
    };
  } catch (error) {
    console.error('Get App Users With Filter Error:', error);
    throw error;
  }
};

export const createAppUser = async (userData) => {
  const response = await api.post("/admin/create-app-user", userData);
  return response.data;
};

export const deactivateUserSubscription = async (userSubscriptionId) => {
  try {
    const response = await api.post(`/deactivate-user-subscription/${userSubscriptionId}`, {
      id: userSubscriptionId
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      try {
        const fallback1 = await api.post("/deactivateUserSubscription", {
          UserSubscriptionID: userSubscriptionId,
          id: userSubscriptionId
        });
        return fallback1.data;
      } catch (err2) {
        if (err2.response?.status === 404) {
          const fallback2 = await api.post(`/admin/deactivateUserSubscription/${userSubscriptionId}`, {
            UserSubscriptionID: userSubscriptionId
          });
          return fallback2.data;
        }
        throw err2;
      }
    }
    throw error;
  }
};

export default api;
